import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchWorksheets } from "../worksheetListSlice";
import { retrieveWorksheet } from "../worksheetSlice";
import { fetchLevels, fetchStudents } from "../../student/studentSlice";

import { formatDate } from "../../../utils/textUtils";
import { refinePages } from "../../../utils/paginationUtils";
import {
  ASSIGNMENT_PLAN_REQUIRED_MESSAGE,
  canCreateAssignment,
} from "../../../utils/planUtils";

import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import WorksheetModal from "./WorksheetModal/WorksheetModal";
import BankAssignModal from "../../problem-bank/BankAssignModal";
import apiClient from "../../../services/apiClient";
import Pagination from "../../../components/Pagination";
import WorksheetSearchBar from "./common/WorksheetSearchBar";
import WorksheetSelectBar from "./common/WorksheetSelectBar";
import WorksheetTypeBox from "./common/WorksheetTypeBox";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";
import "./WorksheetTable.css";

const thStyle = {
  padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px",
  fontSize: "0.78rem",
  fontWeight: 700,
  color: "#7A8490",
  whiteSpace: "nowrap",
};
const tdStyle = { padding: Math.min(window.innerWidth, window.screen.width) <= 768 ? "9px 10px" : "13px 16px", fontSize: "0.92rem" };

const iconBtnStyle = {
  width: "34px",
  height: "34px",
  backgroundColor: "#FFF",
  border: "1.5px solid #E6EDEC",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "#3A434E",
};

const WorksheetTable = () => {
  const dispatch = useDispatch();

  const [isMobileW, setIsMobileW] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  useEffect(() => {
    const f = () => setIsMobileW(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);

  const { results, demo_results, current_page, total_pages, previous_created_at } =
    useSelector((state) => state.worksheets);
  const { allStudents } = useSelector((state) => state.student);
  const { user } = useSelector((state) => state.auth);

  const canAssign = canCreateAssignment(user.plan);

  const [keyword, setKeyword] = useState("");
  const [typeSelect, setTypeSelect] = useState("all");
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  // 지금 연 것이 체험용 문제지인지. 목록 줄에만 있는 표시라 모달로 들고 간다.
  const [demoWorksheet, setDemoWorksheet] = useState(null);
  // 문제뱅크에서 만든 시험지. 지인에듀 문제지와 같은 표에 얹어 보여 주고, 출제도
  // 여기서 한다. 예전에는 '출제 문제지 목록' 안에 따로 상자를 세워 두어서
  // 만들어 놓고도 어디서 꺼내는지 알기 어려웠다.
  const [bankSets, setBankSets] = useState([]);
  const [bankTarget, setBankTarget] = useState(null);

  const loadBankSets = () => {
    // 공용 apiClient: 토큰이 만료돼 있어도 갱신 후 재시도해 목록이 빈다지 않는다
    apiClient
      .get("/api/v1/echo/bank/sets/", { withAuth: true })
      .then((response) => setBankSets(response.data.ok ? response.data.sets : []))
      .catch(() => {});
  };
  useEffect(loadBankSets, []);

  // 문제뱅크 시험지를 목록 줄 모양으로 맞춘다 (체험 줄과 같은 방식).
  const bankRows = (bankSets || [])
    .filter((one) => keyword === "" || one.title.includes(keyword))
    .filter((one) => typeSelect === "all")
    .map((one) => ({
      // 문항 번호 칸. 지인에듀 번호 대신 종류 글자+번호(G0035)를 보여 준다.
      // 나중에 시험지를 찾을 때 이 번호로 부른다 (토큰은 링크라 노출하지 않는다).
      id: one.display_code || "-",
      token: one.token,
      title: one.title,
      type_name: "bank",
      created_at: one.created_at,
      user_name: one.owner_name || "문제뱅크",
      is_bank: true,
      is_bank_demo: Boolean(one.is_demo),
      kind_name: one.kind_name,
      item_count: one.item_count,
    }));

  // 문제뱅크 시험지를 지인에듀 문제지 사이에 **만든 날짜 순서로** 끼워 넣는다.
  // 예전에는 늘 표 맨 위에 몰려 있어서 최근에 만든 지인에듀 문제지가 밀려 내려갔다
  // (2026-09-28 사장님 지적). 지인에듀 목록은 서버가 페이지로 나눠 주므로, 뱅크 줄도
  // 이 페이지의 날짜 구간에 드는 것만 보인다:
  //   [이 페이지 맨 아래 줄, 앞 페이지 마지막 줄)  (첫 페이지는 위쪽 끝이 없고,
  //   마지막 페이지는 아래쪽 끝이 없다)
  // 날짜는 양쪽 모두 "2026/09/22 14:09:55" 모양이라 글자로 견준다(뱅크는 초가 없다).
  const pageRows = useMemo(() => {
    const dateKey = (value) => {
      const text = String(value || "");
      return text.length === 16 ? `${text}:00` : text;
    };
    const ginedu = results || [];
    const isFirstPage = Number(current_page) <= 1;
    const isLastPage = Number(current_page) >= Number(total_pages);
    const oldest = ginedu.length ? dateKey(ginedu[ginedu.length - 1].created_at) : "";
    const upper = isFirstPage ? "" : dateKey(previous_created_at);

    const bankOnPage = bankRows.filter((row) => {
      const key = dateKey(row.created_at);
      if (!ginedu.length) return isFirstPage;
      if (upper && key >= upper) return false;
      if (!isLastPage && key < oldest) return false;
      return true;
    });

    return [
      ...ginedu.map((item) => ({ item, rowKey: item.id })),
      ...bankOnPage.map((item) => ({ item, rowKey: `bank-${item.token}` })),
    ].sort((a, b) => (dateKey(b.item.created_at) > dateKey(a.item.created_at) ? 1 : -1));
    // bankRows 는 렌더마다 새로 만들어지므로 재료(bankSets·검색·종류)로 건다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, bankSets, keyword, typeSelect, current_page, total_pages, previous_created_at]);

  useEffect(() => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page: 1, type: typeParam, keyword: keyword }));
    if (allStudents.length == 0) {
      dispatch(fetchStudents());
      dispatch(fetchLevels());
    }
  }, [dispatch, typeSelect, keyword]);

  const visiblePages = useMemo(
    () => refinePages({ total_pages, current_page }),
    [current_page, total_pages]
  );

  const handleWorksheetClick = async (item) => {
    await dispatch(
      retrieveWorksheet({
        id: item.id,
        type: item.type_name,
        author_name: item.user_name,
      })
    );
    setDemoWorksheet(item.is_demo ? item : null);
    setAssignmentModalOpen(true);
  };

  const handlePageClick = (page) => {
    const typeParam = typeSelect === "all" ? null : typeSelect;
    dispatch(fetchWorksheets({ page, type: typeParam, keyword: keyword }));
  };

  const handlePreviewClick = (id, type) => {
    window.open(`https://www.ginedu.com/${type}/${id}/`, "_blank");
  };


  // 체험 줄과 본인 문제지 줄은 모양이 같으므로 한 곳에서 그린다.
  const renderWorksheetRow = (item, rowKey) => (
    <tr
      key={rowKey}
      style={{ borderBottom: "1px solid #E6EDEC" }}
    >
      <td
        style={{
          ...tdStyle,
          color: "#7A8490",
          fontSize: "0.84rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {item.id}
      </td>
      <td
        style={{
          ...tdStyle,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          fontWeight: 700,
          color: "#0E141B",
        }}
      >
        {item.title}
        {/* 문제뱅크 줄은 "문제뱅크" 대신 종류(문법·단어...)만 단다. 문항 수도
            안 보여 준다 (목록에서는 필요 없다는 피드백, 2026-08-24). */}
        <WorksheetTypeBox
          type={item.type_name}
          label={item.is_bank ? item.kind_name : undefined}
        />
        {item.is_bank_demo && (
          <span className="worksheet-table__demo-badge">체험</span>
        )}
        {item.is_demo && (
          <span className="worksheet-table__demo-badge">체험</span>
        )}
      </td>
      <td
        style={{
          ...tdStyle,
          color: "#3A434E",
          fontSize: "0.86rem",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatDate(item.created_at)}
      </td>
      <td style={{ ...tdStyle, color: "#3A434E" }}>
        {item.user_name}
      </td>
      <td style={{ position: "relative" }}>
        <button
          onClick={() =>
            item.is_bank
              ? window.open(`/teacher/bank-paper.html?t=${item.token}`, "_blank")
              : handlePreviewClick(item.id, item.type_name)
          }
          style={iconBtnStyle}
          title="문서 열기"
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#00D9C0";
            e.currentTarget.style.color = "#00A396";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#E6EDEC";
            e.currentTarget.style.color = "#3A434E";
          }}
        >
          <DescriptionIcon style={{ fontSize: "20px" }} />
        </button>
      </td>
      <td style={{ textAlign: "center" }}>
        <button
          style={{
            backgroundColor: "#0E141B",
            color: "#FFFFFF",
            border: "none",
            borderRadius: "999px",
            padding: "8px 20px",
            cursor: "pointer",
            fontSize: "0.84rem",
            fontWeight: 700,
            fontFamily: "inherit",
            transition: "background-color .12s",
            whiteSpace: "nowrap",
          }}
          onClick={() => {
            // 플랜이 안 되는 사람에게도 버튼은 그대로 보여주고, 눌렀을 때
            // 안내한다. 예시 문제지만은 체험할 수 있게 열어 둔다.
            // 체험 줄(지인에듀 예시·문제뱅크 홍길동)은 플랜과 무관하게 눌러 볼 수
            // 있어야 한다. 이 관문이 체험 분기보다 앞이라 basic 이 체험도 못 했다.
            if (!canAssign && !item.is_demo && !item.is_bank_demo) {
              alert(ASSIGNMENT_PLAN_REQUIRED_MESSAGE);
              return;
            }
            if (item.is_bank && item.is_bank_demo) {
              if (!window.confirm("체험 시험지는 홍길동(체험 학생)에게 출제돼요. 출제할까요?")) return;
              apiClient
                .post("/api/v1/echo/bank/assign/", { t: item.token }, { withAuth: true })
                .then((response) => response.data)
                .then((response) => {
                  if (!response.ok) {
                    alert(response.error || "출제하지 못했어요");
                    return;
                  }
                  alert("홍길동에게 출제했어요. 출제 문제지 목록에서 결과를 볼 수 있어요.");
                })
                .catch(() => alert("출제하지 못했어요"));
              return;
            }
            if (item.is_bank) {
              setBankTarget(item);
              return;
            }
            handleWorksheetClick(item);
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#00A396";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#0E141B";
          }}
          title={
            item.is_demo
              ? "출제 화면을 체험해 봅니다 (실제로 출제되지 않습니다)"
              : canAssign
              ? "과제 출제하기"
              : "Plus, Premium 사용자만 출제 가능합니다"
          }
        >
          {item.is_demo || item.is_bank_demo ? "체험하기" : "출제하기"}
        </button>
      </td>
    </tr>
  );

  return (
    <div
      style={{
        padding: "26px 22px 44px",
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
      }}
    >
      {/* 페이지 헤더 */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: isMobileW ? "center" : "flex-end",
          gap: "8px",
          marginBottom: "18px",
          flexWrap: "nowrap",
        }}
      >
        {!isMobileW && (
          <h3 className="section-title">
            문제지 전체 목록
          </h3>
        )}
        <WorksheetSelectBar
          typeSelect={typeSelect}
          setTypeSelect={setTypeSelect}
        />
        <div style={{ flex: isMobileW ? 1 : "none", width: isMobileW ? "auto" : "30%", minWidth: isMobileW ? 0 : "220px" }}>
          <WorksheetSearchBar handleFilter={setKeyword} />
        </div>
      </div>

      {/* 표 카드 */}
      <div
        style={{
          backgroundColor: "#FFF",
          border: "1px solid #E6EDEC",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
          paddingBottom: "5px",
          overflowX: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table style={{ width: "100%", minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "750px" : undefined, borderCollapse: "collapse" }}>
          <colgroup>
            <col style={{ width: "10%" }} />
            <col />
            <col style={{ width: "8%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <tbody>
            <tr
              style={{
                textAlign: "left",
                borderBottom: "1.5px solid #0E141B",
              }}
            >
              <td style={thStyle}>문항 번호</td>
              <td style={thStyle}>학습지명</td>
              <td style={thStyle}>생성일</td>
              <td style={thStyle}>출제자</td>
              <td style={{ ...thStyle, textAlign: "center" }}>미리보기</td>
              <td style={{ ...thStyle, textAlign: "center" }}>출제</td>
            </tr>
            {demo_results &&
              demo_results.map((item) =>
                renderWorksheetRow(item, `demo-${item.id}`)
              )}
            {pageRows.map(({ item, rowKey }) => renderWorksheetRow(item, rowKey))}
          </tbody>
        </table>

        <Pagination
          visiblePages={visiblePages}
          current_page={current_page}
          handlePageClick={handlePageClick}
        />

        {assignmentModalOpen && (
          <WorksheetModal
            isDemo={Boolean(demoWorksheet)}
            onClose={() => setAssignmentModalOpen(false)}
          ></WorksheetModal>
        )}

        {bankTarget && (
          <BankAssignModal
            bankSet={bankTarget}
            onClose={() => setBankTarget(null)}
            onDone={loadBankSets}
          />
        )}
      </div>
    </div>
  );
};

export default WorksheetTable;
